import { strIsNullOrEmpty } from '@fox/atoms';
import { Customer } from '@fox/finance';
import { EventCapacityData } from '@fox/forms-runtime';
import { MemberEntityData } from '../../member-entity-data';
import { SwiftFoxTenantConfig } from '../../swiftfox-tenant-config-context';
import { SwiftFoxFormDefinition } from '../../SwiftFoxForm/contracts';
import { SwiftFoxActionCentreColumnsResult } from '../action-centre/contracts';
import { SwiftFoxEventDetailed, SwiftFoxEventResult, SwiftFoxRsvpOption } from '../events/contracts';
import {
	LineItem,
	Receipt,
	SwiftFoxInvoiceDetailed,
	SwiftFoxInvoiceResult,
	SwiftFoxPlanOption,
	SwiftFoxPlansResult,
	SwiftFoxReceiptResult,
	SwiftFoxSubscriptionDetailed,
	SwiftFoxSubscriptionLineItemResult,
	SwiftFoxSubscriptionResult,
	SwiftFoxTableSettings
} from '../financial-details/contracts';
import {
	SwiftFoxGoalPointsCategory,
	SwiftFoxGoalsActivitiesResult,
	SwiftFoxGoalsConfig,
	SwiftFoxGoalsGoal,
	SwiftFoxGoalsPointsSnapshotResult,
	SwiftFoxGoalsResult
} from '../goals/contracts';
import { CustomFieldModel, ImportProfile, LinkedOrganisationDetails } from '../importer/contracts';
import { SwiftFoxEntitySummary, SwiftFoxPersonDetails, SwiftFoxSearchResult } from '../membership-form-template/contracts';
import { EditRules } from '../profiles/shared/contracts';
import { EntityInfo, RepresentativeUnit } from '../representatives/contracts';
import {
	ActionCentreSourcesQueryHookImplementationProps,
	CustomerQueryHookImplementationProps,
	CustomFieldModelQueryHookImplementationProps,
	EditRulesQueryHookImplementationProps,
	EntitySearchQueryHookImplementationProps,
	EventQueryHookImplementationProps,
	EventsQueryHookImplementationProps,
	GoalPointsCategoriesQueryHookImplementationProps,
	GoalsActivitiesQueryHookImplementationProps,
	GoalsByOwnerTypeQueryHookImplementationProps,
	GoalsQueryHookImplementationProps,
	GoalsSnapshotQueryHookImplementationProps,
	ImportProfilesQueryHookImplementationProps,
	InvoiceQueryHookImplementationProps,
	InvoicesQueryHookImplementationProps,
	QueryHookImplementationProps,
	ReceiptQueryHookImplementationProps,
	ReceiptsQueryHookImplementationProps,
	RepresentativeRelationshipsQueryHookImplementationProps,
	SubscriptionPayableLineItemsQueryHookImplementationProps,
	SubscriptionQueryHookImplementationProps,
	SubscriptionsPayableLineItemsQueryHookImplementationProps,
	SubscriptionsQueryHookImplementationProps,
	SwiftFoxEntitySummaryQueryHookImplementationProps,
	SwiftFoxFormDefinitionQueryHookImplementationProps,
	TableSettingsQueryHookImplementationProps,
	useQuery,
	VerifyImportQueryHookImplementationProps
} from './query-template';

/** fetches the SwifTFox addresstypes for the tenant */
export const useAddressTypes = (input: QueryHookImplementationProps<string[]>) => {
	const { data, loading, error, refetch } = useQuery<string[]>({
		...input,
		url: `/Umbraco/Api/Membership/GetSwiftFoxAddressTypes?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const useCustomer = (input: CustomerQueryHookImplementationProps<Customer[]>) => {
	let url = `/Umbraco/Api/Membership/PaymentDetails?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<Customer[]>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useEditRules = (input: EditRulesQueryHookImplementationProps<EditRules>) => {
	let url = `/Umbraco/Api/Membership/MembershipEditRules?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<EditRules>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

/** fetches the details of the member currently logged in */
export const useMember = (input: QueryHookImplementationProps<SwiftFoxPersonDetails>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxPersonDetails>({
		...input,
		url: `/Umbraco/Api/Membership/GetPerson?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const useEntitySummary = (input: SwiftFoxEntitySummaryQueryHookImplementationProps<SwiftFoxEntitySummary>) => {
	let url = `/Umbraco/Api/Membership/GetEntitySummary?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxEntitySummary>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const getMember = async (input: { contentId: string; requestToken: string }) => {
	const { contentId, requestToken } = input;
	const memberRes = await fetch(`/Umbraco/Api/Membership/GetPerson?contentId=${contentId}`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			RequestVerificationToken: requestToken
		}
	});
	if (memberRes.ok) {
		return await memberRes.json();
	} else {
		return await memberRes.text();
	}
};

export const usePlans = (input: QueryHookImplementationProps<SwiftFoxPlansResult[]>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxPlansResult[]>({
		...input,
		url: `/Umbraco/Api/Membership/Plans?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const useReceipts = (input: ReceiptsQueryHookImplementationProps<SwiftFoxReceiptResult>) => {
	let url = `/Umbraco/Api/Membership/Receipts?contentId=${input.contentId}&page=${input.page ?? 1}&pageSize=${input.pageSize ?? 10}&asc=${
		input.sort?.asc ?? false
	}&column=${input.sort?.column ?? 'createdOn'}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}
	if (input.allowedProviders?.length) {
		url += input.allowedProviders.map(provider => `&allowedProviders=${encodeURIComponent(provider)}`).join('');
	}
	if (!strIsNullOrEmpty(input.replacedBy)) {
		url += `&replacedBy=${input.replacedBy}`;
	}
	if (input.findFailed) {
		url += `&findFailed=${input.findFailed}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxReceiptResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useReceipt = (input: ReceiptQueryHookImplementationProps<Receipt>) => {
	let url = `/Umbraco/Api/Membership/Receipt?contentId=${input.contentId}&id=${input.id}`;

	const { data, loading, error, refetch } = useQuery<Receipt>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useInvoices = (input: InvoicesQueryHookImplementationProps<SwiftFoxInvoiceResult>) => {
	let url = `/Umbraco/Api/Membership/Invoices?contentId=${input.contentId}&page=${input.page ?? 1}&pageSize=${input.pageSize ?? 10}&asc=${
		input.sort?.asc ?? false
	}&column=${input.sort?.column ?? 'createdOn'}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxInvoiceResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useInvoice = (input: InvoiceQueryHookImplementationProps<SwiftFoxInvoiceDetailed>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxInvoiceDetailed>({
		...input,
		url: `/Umbraco/Api/Membership/Invoice?contentId=${input.contentId}&invoiceId=${input.invoiceId}`
	});

	return { data, loading, error, refetch };
};

export const useSubscription = (input: SubscriptionQueryHookImplementationProps<SwiftFoxSubscriptionDetailed>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxSubscriptionDetailed>({
		...input,
		url: `/Umbraco/Api/Membership/Subscription?contentId=${input.contentId}&subscriptionId=${input.subscriptionId}`
	});

	return { data, loading, error, refetch };
};

export const useSubscriptionPayableLineItems = (input: SubscriptionPayableLineItemsQueryHookImplementationProps<LineItem[]>) => {
	const { data, loading, error, refetch } = useQuery<LineItem[]>({
		...input,
		url: `/Umbraco/Api/Membership/SubscriptionPayableLineItems?contentId=${input.contentId}&subscriptionId=${input.subscriptionId}`
	});

	return { data, loading, error, refetch };
};

export const useSubscriptionsPayableLineItems = (
	input: SubscriptionsPayableLineItemsQueryHookImplementationProps<SwiftFoxSubscriptionLineItemResult[]>
) => {
	let url = `/Umbraco/Api/Membership/SubscriptionsPayableLineItems?contentId=${input.contentId}`;
	if (input.subscriptionIds?.length) {
		url += input.subscriptionIds.map(option => `&subscriptionIds=${encodeURIComponent(option)}`).join('');
	}
	const { data, loading, error, refetch } = useQuery<SwiftFoxSubscriptionLineItemResult[]>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useSubscriptions = (input: SubscriptionsQueryHookImplementationProps<SwiftFoxSubscriptionResult>) => {
	let url = `/Umbraco/Api/Membership/Subscriptions?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxSubscriptionResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useEvents = (input: EventsQueryHookImplementationProps<SwiftFoxEventResult>) => {
	let url = `/Umbraco/Api/Membership/Events?contentId=${input.contentId}`;
	if (input.search) {
		url += `&search=${encodeURIComponent(input.search)}`;
	}
	if (input.startDate) {
		url += `&startDate=${encodeURIComponent(input.startDate)}`;
	}
	if (input.endDate) {
		url += `&endDate=${encodeURIComponent(input.endDate)}`;
	}
	if (input.page !== undefined && input.pageSize !== undefined) {
		url += `&page=${input.page}&pageSize=${input.pageSize}`;
	}
	if (input.rsvpOptions?.length) {
		url += input.rsvpOptions.map(option => `&rsvpOptions=${encodeURIComponent(option)}`).join('');
	}
	if (input.locations?.length) {
		url += input.locations.map(location => `&locations=${encodeURIComponent(location)}`).join('');
	}
	if (input.states?.length) {
		url += input.states.map(state => `&states=${encodeURIComponent(state)}`).join('');
	}
	const { data, loading, error, refetch } = useQuery<SwiftFoxEventResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useTableSettings = (input: TableSettingsQueryHookImplementationProps<SwiftFoxTableSettings[]>) => {
	let url = `/Umbraco/Api/Membership/TableSettings?contentId=${input.contentId}`;

	if (input.tableNames?.length) {
		url += input.tableNames.map(tableName => `&tableNames=${encodeURIComponent(tableName)}`).join('');
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxTableSettings[]>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useEvent = (input: EventQueryHookImplementationProps<SwiftFoxEventDetailed>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxEventDetailed>({
		...input,
		url: `/Umbraco/Api/Membership/Event?contentId=${input.contentId}&eventId=${encodeURIComponent(input.eventId)}`
	});

	return { data, loading, error, refetch };
};

export const useRsvpOptions = (input: QueryHookImplementationProps<SwiftFoxRsvpOption[]>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxRsvpOption[]>({
		...input,
		url: `/Umbraco/Api/Membership/RsvpOptions?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const usePlanOptions = (input: QueryHookImplementationProps<SwiftFoxPlanOption[]>) => {
	const { data, loading, error, refetch } = useQuery<SwiftFoxPlanOption[]>({
		...input,
		url: `/Umbraco/Api/Membership/PlanOptions?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const useActionCentreSources = (input: ActionCentreSourcesQueryHookImplementationProps<Record<string, string>>) => {
	let url = `/Umbraco/Api/Membership/ActionCentreSources?contentId=${input.contentId}&actionId=${encodeURIComponent(input.actionId)}`;

	const { data, loading, error, refetch } = useQuery<Record<string, string>>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useActionCentreColumns = (input: ActionCentreSourcesQueryHookImplementationProps<SwiftFoxActionCentreColumnsResult>) => {
	let url = `/Umbraco/Api/Membership/ActionCentreColumns?contentId=${input.contentId}&actionId=${encodeURIComponent(input.actionId)}`;

	const { data, loading, error, refetch } = useQuery<SwiftFoxActionCentreColumnsResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useEntitySearch = (input: EntitySearchQueryHookImplementationProps<SwiftFoxSearchResult[]>) => {
	let url = `/Umbraco/Api/Membership/SwiftFoxEntities?contentId=${input.contentId}`;
	if (input.searchString) {
		url += `&searchString=${encodeURIComponent(input.searchString)}`;
	}

	if (input.types?.length) {
		url += input.types.map(type => `&types=${encodeURIComponent(type)}`).join('');
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxSearchResult[]>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useRepresentativeData = (input: QueryHookImplementationProps<RepresentativeUnit[]>) => {
	const { data, loading, error, refetch } = useQuery<RepresentativeUnit[]>({
		...input,
		url: `/Umbraco/Api/Membership/GetRepresentativeUnits?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const useRepresentativeRelationsData = (
	input: RepresentativeRelationshipsQueryHookImplementationProps<{ data: EntityInfo[]; totalResults: number }>
) => {
	let url = `/Umbraco/Api/Membership/GetRepresentativeRelationships?contentId=${input.contentId}&sfOrgId=${input.sfOrgId}&representativeUnitId=${input.representativeUnitId}&page=${input.page}&pageSize=${input.pageSize}`;
	if (input.representativeOrgIds?.length) {
		url += input.representativeOrgIds.map(orgId => `&representativeOrgIds=${encodeURIComponent(orgId)}`).join('');
	}
	if (!strIsNullOrEmpty(input.search)) {
		url += `&search=${encodeURIComponent(input.search)}`;
	}
	const { data, loading, error, refetch } = useQuery<{ data: EntityInfo[]; totalResults: number }>({
		...input,
		url
	});
	if (data?.data) {
		data.data = data.data?.sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0)) ?? [];
	}
	return { data, loading, error, refetch };
};

export const useCustomFieldModel = (input: CustomFieldModelQueryHookImplementationProps<CustomFieldModel[]>) => {
	const { data, loading, error, refetch } = useQuery<CustomFieldModel[]>({
		...input,
		url: `/Umbraco/Api/Membership/CustomFieldModel?contentId=${input.contentId}&type=${input.type}`
	});

	return { data, loading, error, refetch };
};

export const useImportProfiles = (input: ImportProfilesQueryHookImplementationProps<ImportProfile[]>) => {
	const { data, loading, error, refetch } = useQuery<ImportProfile[]>({
		...input,
		url: `/Umbraco/Api/Membership/ImportProfiles?contentId=${input.contentId}&category=${input.category}`
	});

	return { data, loading, error, refetch };
};

export const useGetLinkedOrganisationDetails = (input: QueryHookImplementationProps<LinkedOrganisationDetails>) => {
	const { data, loading, error, refetch } = useQuery<LinkedOrganisationDetails>({
		...input,
		url: `/Umbraco/Api/Membership/LinkedOrganisationDetails?contentId=${input.contentId}`
	});

	return { data, loading, error, refetch };
};

export const useVerifyImport = (input: VerifyImportQueryHookImplementationProps<boolean>) => {
	let url = `/Umbraco/Api/Membership/VerifyImport?contentId=${input.contentId}&organisationId=${encodeURIComponent(
		input.organisationId
	)}&numberOfRecordsImported=${encodeURIComponent(input.numberOfRecordsImported)}&id=${encodeURIComponent(input.id)}`;

	const { data, loading, error, refetch } = useQuery<boolean>({
		...input,
		url
	});
	return { data, loading, error, refetch };
};

export const useGoals = (input: GoalsQueryHookImplementationProps<SwiftFoxGoalsResult>) => {
	let url = `/Umbraco/Api/Membership/Goals?contentId=${input.contentId}&page=${input.page ?? 1}&pageSize=${input.pageSize ?? 10}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxGoalsResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useGoalsByOwnerType = (input: GoalsByOwnerTypeQueryHookImplementationProps<SwiftFoxGoalsGoal[]>) => {
	let url = `/Umbraco/Api/Membership/GoalsByOwnerType?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxGoalsGoal[]>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useGoalPointsCategories = (input: GoalPointsCategoriesQueryHookImplementationProps<SwiftFoxGoalPointsCategory[]>) => {
	let url = `/Umbraco/Api/Membership/PointCategories?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxGoalPointsCategory[]>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useGoalsActivities = (input: GoalsActivitiesQueryHookImplementationProps<SwiftFoxGoalsActivitiesResult>) => {
	let url = `/Umbraco/Api/Membership/GoalsActivities?contentId=${input.contentId}&page=${input.page ?? 1}&pageSize=${
		input.pageSize ?? 10
	}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxGoalsActivitiesResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useGoalsSnapShot = (input: GoalsSnapshotQueryHookImplementationProps<SwiftFoxGoalsPointsSnapshotResult>) => {
	let url = `/Umbraco/Api/Membership/GoalsSnapshot?contentId=${input.contentId}`;

	if (input.useLinkedOrganisationData) {
		url += `&useLinkedOrganisationData=${input.useLinkedOrganisationData}`;
	}

	const { data, loading, error, refetch } = useQuery<SwiftFoxGoalsPointsSnapshotResult>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useGoalsConfig = (input: QueryHookImplementationProps<SwiftFoxGoalsConfig>) => {
	let url = `/Umbraco/Api/Membership/GoalsConfig?contentId=${input.contentId}`;

	const { data, loading, error, refetch } = useQuery<SwiftFoxGoalsConfig>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useSwiftFoxFormDefinition = (input: SwiftFoxFormDefinitionQueryHookImplementationProps<SwiftFoxFormDefinition>) => {
	let url = `/Umbraco/Api/Membership/SwiftFoxFormDefinition?contentId=${input.contentId}&formId=${input.formId}`;

	const { data, loading, error, refetch } = useQuery<SwiftFoxFormDefinition>({ ...input, url });

	return { data, loading, error, refetch };
};

export const useEventDetailsPageURL = (input: QueryHookImplementationProps<string>) => {
	const url = `/Umbraco/Api/Membership/EventDetailsPageURL?contentId=${input.contentId}`;

	const { data, loading, error, refetch } = useQuery<string>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useMemberEntityData = (input: QueryHookImplementationProps<MemberEntityData>) => {
	const url = `/Umbraco/Api/Membership/MemberEntityData?contentId=${input.contentId}`;

	const { data, loading, error, refetch } = useQuery<MemberEntityData>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useEventCapacityStatus = (input: EventQueryHookImplementationProps<EventCapacityData>) => {
	const url = `/Umbraco/Api/Membership/EventCapacityStatus?contentId=${input.contentId}&eventId=${encodeURIComponent(input.eventId)}`;

	const { data, loading, error, refetch } = useQuery<EventCapacityData>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};

export const useSwiftFoxTenantConfig = (input: QueryHookImplementationProps<SwiftFoxTenantConfig>) => {
	const url = `/Umbraco/Api/Membership/SwiftFoxTenantConfig?contentId=${input.contentId}`;

	const { data, loading, error, refetch } = useQuery<SwiftFoxTenantConfig>({
		...input,
		url
	});

	return { data, loading, error, refetch };
};